/**
 * Particle texture conversion (authoring-time, editor-only).
 *
 * Particle systems blend with premultiplied-alpha Porter-Duff unconditionally at
 * runtime (see `behaviors/particlesystem.ts` and
 * `docs-internal/particle-alpha-transparency.md`). The texture that reaches the runtime
 * must therefore already be premultiplied RGBA. This module is the single, shared
 * implementation that converts a source image to premultiplied RGBA based on the
 * author's chosen import mode.
 *
 * Editors (trace-sandbox, TraceFrontend) MUST import this rather than reimplementing the
 * pixel math — the two had drifted-prone copies before this was centralised. The runtime
 * never calls this: it trusts `texture` is already premultiplied.
 *
 * The import mode and the original source are authoring state only. They are NOT stored in
 * scene data — only the baked premultiplied texture is. See
 * `docs-internal/particle-texture-format.md` (option F).
 */

import { ParticleTextureMode } from '../scenedata/behaviors/particledata';

/**
 * Recommended maximum particle texture side, in pixels. Particle sprites are small on
 * screen and replicated across many particles; sides above this waste GPU memory and
 * upload bandwidth for no visible benefit. Editors warn above this and offer to fit to it.
 */
export const PARTICLE_TEXTURE_MAX_PX = 256;

/**
 * Largest power-of-two ≤ n. Returns 1 for any n < 1 (no valid PO2 below 1). Caps at 2³⁰ for
 * n ≥ 2³¹, where the `1 << k` shift would overflow signed-32-bit into a negative — far beyond
 * any real texture side, but guarded so the result is never surprising.
 */
export const prevPowerOfTwo = (n: number): number =>
    n < 1 ? 1 : n >= 0x80000000 ? 0x40000000 : 1 << Math.floor(Math.log2(n));

/** True when n is a positive power of two. */
export const isPowerOfTwo = (n: number): boolean => n > 0 && (n & (n - 1)) === 0;

/**
 * Nearest power-of-two to n, measured in log space (so the split point between two PO2s is
 * their geometric midpoint, `lo·√2`). Returns 1 for any n < 1. e.g. 170 → 128, 200 → 256.
 * Capped at 2³⁰ to match {@link prevPowerOfTwo} (beyond any real texture side; keeps the public
 * result finite and bounded rather than returning a huge value / Infinity for absurd inputs).
 */
export const nearestPowerOfTwo = (n: number): number =>
    n < 1 ? 1 : Math.min(2 ** Math.round(Math.log2(n)), 0x40000000);

/**
 * The side a particle texture dimension should be reduced to so it is power-of-two and no
 * larger than `maxPx`: take the next-lowest power of two, then clamp to the next-lowest
 * power of two of `maxPx`. e.g. 300 → 256, 600 → 512 → 256, 100 → 64.
 *
 * The clamp uses `prevPowerOfTwo(maxPx)` rather than `maxPx` directly so the result stays a
 * power of two even when a caller passes a non-PO2 cap (e.g. 300 → 256, not 300).
 *
 * This is the single-axis test used by the editor warning (is each side PO2 and within the
 * cap?). The actual resize target — which couples the two axes to preserve aspect ratio —
 * comes from {@link fitParticleTextureDimensions}.
 */
export const fitParticleTextureSide = (n: number, maxPx = PARTICLE_TEXTURE_MAX_PX): number =>
    Math.min(prevPowerOfTwo(n), prevPowerOfTwo(maxPx));

/** Options for the particle-texture fit. */
export interface ParticleTextureFitOptions {
    /** Largest output side, in pixels. Defaults to {@link PARTICLE_TEXTURE_MAX_PX}. */
    maxPx?: number;
    /**
     * Force a square (1:1) output. Default `true`.
     *
     * Babylon's CPU `ParticleSystem` renders every particle on a SQUARE billboard quad (`size`
     * is scalar — no per-axis width/height), so a non-square texture is stretched to fill the
     * square and looks squished. Until the data spec carries an aspect ratio (and we drive
     * `minScaleX/Y`/`maxScaleX/Y` from it), the safe default is to bake a square texture: the
     * content keeps its aspect via uniform scale and is centred with transparent padding on the
     * short axis. Pass `false` to get the aspect-preserving (crop/pad to nearest-PO2) target
     * instead — reserved for when aspect-aware rendering lands.
     */
    square?: boolean;
}

/**
 * The power-of-two target dimensions a particle texture should be resized to, ≤ `maxPx` per
 * side. This is the single source of truth shared by the resize ({@link fitParticleTextureToPo2})
 * and the editor's button label, so the preview and the result always agree. Content is never
 * distorted — a single uniform scale is applied to both axes.
 *
 * **Square mode (default — see {@link ParticleTextureFitOptions.square}):** both sides equal the
 * **longer** side's next-lowest PO2 (capped at `maxPx`). The shorter side, scaled by the same
 * factor, is centred with transparent padding. e.g. 600×300 → 256×256. No cropping.
 *
 * **Aspect mode (`square: false`):** the longer side maps to its PO2 target; the shorter side,
 * scaled by the same factor, snaps to {@link nearestPowerOfTwo} of its scaled length — rounding
 * down centre-crops, rounding up transparent-pads (≈29% worst case, the √2 midpoint). e.g.
 * 600×300 → 256×128.
 *
 * Both returned sides are always powers of two: each is the min of two PO2 values — `prevPowerOfTwo`
 * (or `nearestPowerOfTwo`) of a length, and `cap = prevPowerOfTwo(maxPx)` — so a non-PO2 `maxPx`
 * can't leak a non-PO2 result.
 */
export const fitParticleTextureDimensions = (
    width: number,
    height: number,
    { maxPx = PARTICLE_TEXTURE_MAX_PX, square = true }: ParticleTextureFitOptions = {},
): { width: number; height: number } => {
    const cap = prevPowerOfTwo(maxPx);
    if (width <= 0 || height <= 0) return { width: 1, height: 1 };

    const longer = Math.max(width, height);
    const shorter = Math.min(width, height);
    const targetLong = Math.min(prevPowerOfTwo(longer), cap);
    if (square) {
        return { width: targetLong, height: targetLong };
    }
    const scale = targetLong / longer;
    const targetShort = Math.min(nearestPowerOfTwo(shorter * scale), cap);

    return width >= height
        ? { width: targetLong, height: targetShort }
        : { width: targetShort, height: targetLong };
};

/** True when an image of these dimensions would change under {@link fitParticleTextureToPo2}. */
export const needsParticleTextureFit = (
    width: number,
    height: number,
    options?: ParticleTextureFitOptions,
): boolean => {
    const fit = fitParticleTextureDimensions(width, height, options);
    return fit.width !== width || fit.height !== height;
};

/**
 * Stable identifier for a particle-texture sizing issue. Editors can key styling/tests off
 * this; the wording is in {@link ParticleTextureWarning.message}.
 *
 * - `non-power-of-two` — a side is not a power of two (GPU sampling/mipmap inefficiency).
 * - `oversized` — a side exceeds the recommended cap (wasted GPU memory / upload).
 * - `non-square` — width ≠ height. Babylon's CPU `ParticleSystem` renders square billboard
 *   quads (scalar `size`), so a non-square texture is stretched to fill the square. Fires even
 *   for a valid power-of-two within the cap (e.g. 256×128) — see {@link fitParticleTextureToPo2}.
 */
export type ParticleTextureWarningCode = 'non-power-of-two' | 'oversized' | 'non-square';

/** A single particle-texture sizing issue: a stable {@link ParticleTextureWarningCode} + copy. */
export interface ParticleTextureWarning {
    code: ParticleTextureWarningCode;
    /** Author-facing message, ready to render. */
    message: string;
}

/**
 * The diagnosis of a baked particle texture's dimensions: every sizing {@link ParticleTextureWarning}
 * that applies, whether a fix is needed, and the square power-of-two target the fix would produce.
 *
 * This is the shared source of truth for the editor warning UI — both trace-sandbox and
 * TraceFrontend render from it, so the conditions, copy, and fix target stay identical across
 * editors and live next to the resize itself. Editors own only the presentation (colours/JSX).
 */
export interface ParticleTextureDiagnosis {
    /** Warnings that apply, in display order (power-of-two, oversized, non-square). Empty = clean. */
    warnings: ParticleTextureWarning[];
    /** True when {@link fitParticleTextureToPo2} would change the texture (i.e. `warnings` non-empty). */
    needsFix: boolean;
    /**
     * The power-of-two dimensions {@link fitParticleTextureToPo2} would produce — square by
     * default, or aspect-preserving when `square: false` was passed (mirrors the fit options).
     */
    fitTarget: { width: number; height: number };
}

/**
 * Diagnose a baked particle texture's dimensions for the editor warning UI. Returns the applicable
 * {@link ParticleTextureWarning}s (with ready-to-render copy), whether a fix is needed, and the
 * target the fix would produce. See {@link ParticleTextureDiagnosis}.
 *
 * `square` defaults true (see {@link ParticleTextureFitOptions}); pass `square: false` to drop the
 * non-square warning when aspect-aware rendering is in use.
 */
export const diagnoseParticleTexture = (
    width: number,
    height: number,
    options: ParticleTextureFitOptions = {},
): ParticleTextureDiagnosis => {
    const { maxPx = PARTICLE_TEXTURE_MAX_PX, square = true } = options;
    const warnings: ParticleTextureWarning[] = [];

    if (!isPowerOfTwo(width) || !isPowerOfTwo(height)) {
        warnings.push({
            code: 'non-power-of-two',
            message: 'Not a power-of-two size — prefer 32/64/128/256 per side.',
        });
    }
    if (width > maxPx || height > maxPx) {
        warnings.push({
            code: 'oversized',
            message: `Larger than ${maxPx}px — particle sprites rarely need this; consider downscaling.`,
        });
    }
    if (square && width !== height) {
        warnings.push({
            code: 'non-square',
            message:
                'Non-square — particles render on a square quad, so this will look stretched. '
                + 'Use a square texture.',
        });
    }

    return {
        warnings,
        needsFix: warnings.length > 0,
        fitTarget: fitParticleTextureDimensions(width, height, options),
    };
};

/**
 * An imported particle texture's original source and the mode it was baked with.
 *
 * This is volatile authoring state: an editor holds it (e.g. in component state) only while
 * a texture's source is still editable this session, so changing the import mode can re-bake
 * the stored `texture` live. It is never serialized — scene data stores only the baked
 * premultiplied texture. See `docs-internal/particle-texture-format.md` (option F).
 */
export interface ParticleSource {
    /** The author's original, unconverted image file. */
    file: File;
    /** Import mode used to bake the current `texture` from `file`. */
    mode: ParticleTextureMode;
}

/**
 * Premultiply an RGBA pixel buffer in place, per import mode.
 *
 * - `Additive`: derive alpha from luminance (`α = max(R,G,B)`, threshold `< 5 → 0`) and
 *   clamp RGB to the premultiplied invariant. For opaque glow textures (JPG, opaque PNG)
 *   where the original alpha carries no usable transparency.
 * - `Alpha`: straight (un-premultiplied) alpha → multiply RGB by `α/255`.
 * - `Premultiplied`: already premultiplied → no-op (identity / escape hatch).
 *
 * After conversion every pixel satisfies `R ≤ A, G ≤ A, B ≤ A`.
 *
 * Defined in integer ops so any consumer (web canvas, Unity C#) can match it pixel-for-pixel.
 */
export function premultiplyPixels(pixels: Uint8ClampedArray, mode: ParticleTextureMode): void {
    if (mode === ParticleTextureMode.Additive) {
        // Derive alpha from luminance: α = max(R,G,B), threshold < 5 → 0
        for (let i = 0; i < pixels.length; i += 4) {
            const a = Math.max(pixels[i], pixels[i + 1], pixels[i + 2]);
            pixels[i + 3] = a < 5 ? 0 : a;
            // Clamp RGB to enforce premultiplied invariant
            if (pixels[i] > pixels[i + 3]) pixels[i] = pixels[i + 3];
            if (pixels[i + 1] > pixels[i + 3]) pixels[i + 1] = pixels[i + 3];
            if (pixels[i + 2] > pixels[i + 3]) pixels[i + 2] = pixels[i + 3];
        }
    } else if (mode === ParticleTextureMode.Alpha) {
        // Straight: premultiply RGB by alpha
        for (let i = 0; i < pixels.length; i += 4) {
            const a = pixels[i + 3] / 255;
            pixels[i] = Math.round(pixels[i] * a);
            pixels[i + 1] = Math.round(pixels[i + 1] * a);
            pixels[i + 2] = Math.round(pixels[i + 2] * a);
        }
    }
    // Premultiplied mode: no-op
}

/** Matches a trailing `-{mode}` import-mode tag (any {@link ParticleTextureMode}, case-insensitive). */
const MODE_TAG_RE = new RegExp(`-(?:${Object.values(ParticleTextureMode).join('|')})$`, 'i');

/**
 * Name a baked particle texture `{base}-{mode}.png`, tagging it with the import mode.
 *
 * Editors store textures under their filename (ResourceManager.addAssetWithDedup), which puts a
 * second bake of the same name (e.g. `marnatar.png`) beside the first as `marnatar-2.png`. That
 * keeps the re-bake reachable, but the name says nothing about the mode, and the next switch goes to
 * `-3`, `-4`… The `-{mode}` tag gives each mode one stable path, so switching back reuses its bake —
 * mirroring the `-{w}x{h}` tag in {@link fitParticleTextureToPo2}.
 *
 * A prior `-{mode}` tag is stripped first so repeated mode switches don't accumulate.
 */
export const tagParticleTextureName = (fileName: string, mode: ParticleTextureMode): string => {
    const base = fileName.replace(/\.\w+$/, '').replace(MODE_TAG_RE, '');
    return `${base}-${mode}.png`;
};

/**
 * Convert an image file to a premultiplied RGBA PNG based on the import mode.
 * Returns a PNG File ready for storage in a ResourceManager. The input file is the author's
 * original source; the returned file is the baked runtime texture.
 *
 * `Premultiplied` mode does NOT re-premultiply (the pixels are already correct) — but the
 * output must still be a PNG, the lossless format that carries an alpha channel:
 *   - An already-PNG source keeps its exact pixels (no canvas round-trip, avoiding any 1-LSB
 *     un/re-premultiply error from getImageData); only the file is re-wrapped under the
 *     mode-tagged name so the stored path stays distinct per mode.
 *   - A non-PNG source (JPEG/WebP — which can't even carry premultiplied alpha) is repacked
 *     into a PNG container without altering pixels.
 *
 * Note: for `Additive`/`Alpha`, the canvas `getImageData` readback un-premultiplies the
 * browser's internally-premultiplied store, so straight-alpha sources can pick up 1-LSB
 * rounding before `premultiplyPixels` runs. Acceptable for authoring; documented so a future
 * cross-engine pixel-exact comparison knows to account for it.
 */
export async function convertToPremultiplied(file: File, mode: ParticleTextureMode): Promise<File> {
    // Already a premultiplied PNG → pass the pixels through losslessly (no canvas round-trip),
    // but still re-wrap under the mode-tagged name so the path stays distinct per mode. Renaming
    // reuses the same blob, so it adds no un/re-premultiply error.
    const taggedName = tagParticleTextureName(file.name, mode);
    if (mode === ParticleTextureMode.Premultiplied && file.type === 'image/png') {
        return file.name === taggedName ? file : new File([file], taggedName, { type: 'image/png' });
    }

    const bitmap = await createImageBitmap(file);
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close();
        throw new Error('2D canvas context unavailable for particle texture conversion');
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    // Premultiplied mode only repacks into a PNG container — it must not re-premultiply.
    if (mode !== ParticleTextureMode.Premultiplied) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        premultiplyPixels(imageData.data, mode);
        ctx.putImageData(imageData, 0, 0);
    }

    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return new File([blob], taggedName, { type: 'image/png' });
}

/**
 * Per-axis placement for one `drawImage` axis, given a source length, the uniform scale shared
 * by both axes, and the power-of-two target for this axis. Returns the source slice
 * (`srcStart`, `srcLen`) and the destination slice (`dstStart`, `dstLen`).
 *
 * - `src·scale ≥ target` → **crop**: take a centred `target/scale` slice of the source, draw it
 *   across the full target (content fills the axis).
 * - `src·scale < target` → **pad**: draw the full source (`src·scale` px) centred in the target;
 *   the leftover is transparent canvas (no pixels drawn there).
 *
 * The longer axis always satisfies `src·scale == target` exactly, so it neither crops nor pads;
 * the shorter axis pads (square mode, or aspect mode rounding up) or crops (aspect mode rounding
 * down).
 *
 * Degenerate guard: for an extreme aspect ratio (e.g. a 10000×1 source squared to 256×256) the
 * padded content would be sub-pixel — `drawImage` could round it to zero and draw nothing. We
 * floor the padded `dstLen` at 1px so at least one row of content survives. Such an input is
 * already nonsensical as a particle sprite; this just keeps it from baking a fully blank texture.
 */
const axisPlacement = (
    src: number,
    scale: number,
    target: number,
): { srcStart: number; srcLen: number; dstStart: number; dstLen: number } => {
    const scaled = src * scale;
    if (scaled >= target) {
        const srcLen = target / scale; // ≤ src
        return { srcStart: (src - srcLen) / 2, srcLen, dstStart: 0, dstLen: target };
    }
    const dstLen = Math.max(scaled, 1);
    return { srcStart: 0, srcLen: src, dstStart: (target - dstLen) / 2, dstLen };
};

/**
 * Fit a particle texture to power-of-two sides no larger than `maxPx`. Resizes to
 * {@link fitParticleTextureDimensions} in a single `drawImage` and returns a PNG `File`.
 *
 * A single uniform scale is applied to both axes, so content is never distorted. By default the
 * output is **square** (see {@link ParticleTextureFitOptions.square}) — Babylon particle quads are
 * square, so a non-square texture would render squished; the content is centred with transparent
 * padding on the short axis (e.g. 600×300 → 256×256). With `square: false` the output preserves
 * aspect ratio, snapping the short side to the nearest PO2 via crop/pad (e.g. 600×300 → 256×128).
 *
 * Premultiplication-safe: pixels are only drawn (composited in the canvas's premultiplied space)
 * and re-encoded — there is no `getImageData`/`putImageData` round-trip, so an already-
 * premultiplied input stays premultiplied with no extra un/re-premultiply rounding. This means it
 * can be applied either to an original source (before {@link convertToPremultiplied}) or directly
 * to an already-baked premultiplied texture. Transparent padding is the premultiplied identity
 * (0,0,0,0), so padding does not break the invariant either.
 *
 * No-op shortcut: if the image already fits (see {@link needsParticleTextureFit}) the input is
 * returned unchanged when it is already a PNG, avoiding a needless re-encode. The bitmap is
 * still decoded first — the no-op check needs the source dimensions, which aren't known until
 * decode — so don't try to hoist the early return above the `createImageBitmap` await.
 *
 * The returned file's name carries a `-{w}x{h}` tag (a prior tag is stripped first so repeated
 * fixes don't accumulate). Editors store textures under their filename, and a fixed texture
 * would otherwise land beside its source as an anonymous `name-2.png`; the dimension tag names
 * the path after what it holds. Note the
 * strip treats ANY trailing `-{digits}x{digits}` as a prior tag, so a source already named like
 * `art-1920x1080.png` loses that suffix — intentional (the tag describes the baked size).
 */
export async function fitParticleTextureToPo2(
    file: File,
    options?: ParticleTextureFitOptions,
): Promise<File> {
    const bitmap = await createImageBitmap(file);
    try {
        if (!needsParticleTextureFit(bitmap.width, bitmap.height, options) && file.type === 'image/png') {
            return file;
        }
        const { width: targetW, height: targetH } = fitParticleTextureDimensions(
            bitmap.width, bitmap.height, options,
        );
        // Uniform scale shared by both axes — fixed by the longer side's exact PO2 fit.
        const scale = bitmap.width >= bitmap.height
            ? targetW / bitmap.width
            : targetH / bitmap.height;
        const x = axisPlacement(bitmap.width, scale, targetW);
        const y = axisPlacement(bitmap.height, scale, targetH);

        const canvas = new OffscreenCanvas(targetW, targetH);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('2D canvas context unavailable for particle texture fit');
        // Set enabled before quality: some engines (older Safari) default enabled to false, in
        // which case the quality hint is ignored and the downsample falls back to nearest-neighbour.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
            bitmap,
            x.srcStart, y.srcStart, x.srcLen, y.srcLen,
            x.dstStart, y.dstStart, x.dstLen, y.dstLen,
        );

        const blob = await canvas.convertToBlob({ type: 'image/png' });
        // Strip a previous `-{w}x{h}` tag, then re-tag with the new dimensions (see JSDoc).
        const base = file.name.replace(/\.\w+$/, '').replace(/-\d+x\d+$/, '');
        return new File([blob], `${base}-${targetW}x${targetH}.png`, { type: 'image/png' });
    } finally {
        bitmap.close();
    }
}
